import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from '@emotion/styled';
import { devices, Spacing, Stack, Text } from '../common';

import { NextPage } from '../SVG';
import { RichTextContent } from '../StyledComponents';
import { ReadMoreButton } from '../../pages';
import { campaignUrlSegmentFromSlug } from '../../utils/campaignSlugPath';
import { campaignShouldShowTopBannerOnOtherPages } from '../../utils/campaignVisibility';

type CampaignBannerData = {
  slug: string;
  published_at: string;
  ShowOnWebsite?: string | null;
  TopBannerVisibleOnOtherPages?: {
    HeaderText?: string;
    ContentText?: string;
    ButtonLabel?: string;
    BackgroundColor?: string;
    FontColor?: string;
  };
};

/** Banner block omitted or empty in Strapi → never show (even if ShowOnWebsite allows). */
function hasCampaignTopBanner(c: CampaignBannerData): boolean {
  const b = c.TopBannerVisibleOnOtherPages;
  if (!b) return false;
  const h = (b.HeaderText || '').trim();
  const ct = (b.ContentText || '').trim();
  const lbl = (b.ButtonLabel || '').trim();
  return !!(h || ct || lbl);
}

const Banner = () => {
  const data = useStaticQuery<{
    allStrapiCampaignPage: {
      nodes: CampaignBannerData[];
    };
  }>(graphql`
    query CampaignBannersForBanner {
      allStrapiCampaignPage(filter: { slug: { ne: null } }) {
        nodes {
          slug
          published_at
          ShowOnWebsite
          TopBannerVisibleOnOtherPages {
            HeaderText
            ContentText
            ButtonLabel
            BackgroundColor
            FontColor
          }
        }
      }
    }
  `);

  const nodes = data?.allStrapiCampaignPage?.nodes ?? [];
  const campaignsWithBanner = nodes
    .filter((c) => campaignShouldShowTopBannerOnOtherPages(c.ShowOnWebsite))
    .filter(hasCampaignTopBanner);
  const campaigns = [...campaignsWithBanner].sort((a, b) => {
    const dateA = a.published_at ? new Date(a.published_at).getTime() : 0;
    const dateB = b.published_at ? new Date(b.published_at).getTime() : 0;
    return dateB - dateA;
  });

  const hasMultipleBanners = campaigns.length > 0;

  return (
    <>
      {/* <BannerBlock>
        <HeaderContainer>
          <Text variant="title-2">Tutustu terapiakäyntiin!</Text>
          <Spacing axis="y" amount="default" />
          <RichTextContent variant="body">
            Aatoksen terapiakäynti on uusi 18-35 vuotiaille suunnattu palvelu,
            joka tarjoaa matalan kynnyksen ensiaskeleen terapiaan.
          </RichTextContent>
          <Spacing axis="y" amount="medium" />
          <ReadMoreButton href="/terapiakaynti/">
            Lue lisää ja varaa aika
            <Spacing axis="x" amount="medium" />
            <NextPage width="30" />
          </ReadMoreButton>
        </HeaderContainer>
      </BannerBlock> */}
      {campaigns.map((campaign) => {
        const b = campaign.TopBannerVisibleOnOtherPages;
        const pathSlug = campaignUrlSegmentFromSlug(campaign.slug);
        return (
          <BannerCampaignGroup key={campaign.slug}>
            {hasMultipleBanners && <BannerDivider />}
            <BannerBlock
              backgroundColor={b?.BackgroundColor}
              fontColor={b?.FontColor}
            >
              <HeaderContainer spacing="none">
                <BannerTextColumn>
                  <Text variant="title-2">{b?.HeaderText}</Text>
                  {b?.ContentText && (
                    <>
                      <Spacing axis="y" amount="default" />
                      <RichTextContent variant="body">
                        {b?.ContentText}
                      </RichTextContent>
                    </>
                  )}
                </BannerTextColumn>
                <ReadMoreButton href={`/kampanja/${pathSlug}/`}>
                  {b?.ButtonLabel}
                  <Spacing axis="x" amount="medium" />
                  <NextPage width="30" />
                </ReadMoreButton>
              </HeaderContainer>
            </BannerBlock>
          </BannerCampaignGroup>
        );
      })}
    </>
  );
};

export default Banner;

/** Wrapper for list key; does not affect layout (children participate in parent flex as before). */
const BannerCampaignGroup = styled.div`
  display: contents;
`;

const BannerDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #000;
`;

const BannerBlock = styled.div<{
  backgroundColor?: string;
  fontColor?: string;
}>`
  background-color: ${(props) =>
    props.backgroundColor ?? props.theme.colors.darkBlue};
  color: ${(props) => props.fontColor ?? props.theme.colors.white};
  width: 100%;
  box-shadow: 0px 3px 6px #0000000b;

  a {
    color: inherit;
  }
`;

const BannerTextColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderContainer = styled(Stack)`
  width: 100%;
  max-width: 1360px;
  margin-left: auto;
  margin-right: auto;
  padding-block: 20px;
  flex-direction: column;
  align-items: stretch;
  gap: ${(p) => p.theme.spacing.medium};

  @media (min-width: 769px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${(p) => p.theme.spacing.large};
  }

  a {
    text-decoration: none;
  }

  @media ${devices.laptopL} {
    width: 95% !important;
  }
`;
